import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  useTheme
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ClearIcon from '@mui/icons-material/Clear';
import CustomDatePicker from '../Common/CustomDatePicker';
import logger from '../../utils/logger';

const TaskFilter = ({ filters, onFilterChange }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { languages, statuses } = useSelector((state) => state.ui);
  const [localFilters, setLocalFilters] = useState(filters);
  
  // 注意：不在这里调用fetchLanguages和fetchStatuses
  // 因为TaskFilter只在已登录且有任务时显示，语种和状态数据应该在其他地方加载
  
  // 处理输入变化
  const handleChange = (e) => {
    const { name, value } = e.target;
    setLocalFilters((prev) => ({
      ...prev,
      [name]: value
    }));
  };
  
  // 处理日期变更
  const handleDateChange = (name, date) => {
    // 日期格式为yyyy-MM-dd
    const formattedDate = date ? date.toISOString().split('T')[0] : '';
    
    // 更新本地筛选状态
    const newFilters = {
      ...localFilters,
      [name]: formattedDate
    };
    
    // 如果选择的是开始日期，需要验证结束日期
    if (name === 'startDate' && date && localFilters.endDate) {
      const endDate = new Date(localFilters.endDate);
      if (date > endDate) {
        // 如果开始日期晚于结束日期，清空结束日期
        newFilters.endDate = '';
        logger.warn('开始日期不能晚于结束日期，已自动清空结束日期');
      }
    }
    
    // 如果选择的是结束日期，需要验证开始日期
    if (name === 'endDate' && date && localFilters.startDate) {
      const startDate = new Date(localFilters.startDate);
      if (date < startDate) {
        // 如果结束日期早于开始日期，清空开始日期
        newFilters.startDate = '';
        logger.warn('结束日期不能早于开始日期，已自动清空开始日期');
      }
    }
    
    setLocalFilters(newFilters);
  };
  
  // 获取今天的日期（用作最大日期）
  const today = new Date();
  today.setHours(23, 59, 59, 999); // 设置为今天的最后一刻
  
  // 计算开始日期的限制范围
  const getStartDateLimits = () => {
    const limits = {
      maxDate: today
    };
    
    // 如果有结束日期，开始日期不能晚于结束日期
    if (localFilters.endDate) {
      const endDate = new Date(localFilters.endDate);
      endDate.setHours(23, 59, 59, 999);
      limits.maxDate = endDate < today ? endDate : today;
    }
    
    return limits;
  };
  
  // 计算结束日期的限制范围
  const getEndDateLimits = () => {
    const limits = {
      maxDate: today
    };
    
    // 如果有开始日期，结束日期不能早于开始日期
    if (localFilters.startDate) {
      const startDate = new Date(localFilters.startDate);
      startDate.setHours(0, 0, 0, 0); // 设置为开始日期的最开始
      limits.minDate = startDate;
    }
    
    return limits;
  };
  
  // 处理筛选
  const handleFilter = () => {
    onFilterChange(localFilters);
  };
  
  // 处理重置
  const handleReset = () => {
    const resetFilters = {
      startDate: '',
      endDate: '',
      language: 'all',
      status: 'all'
    };
    setLocalFilters(resetFilters);
    onFilterChange(resetFilters);
  };
  
  // 统一的高度和字体样式
  const commonHeight = '37px'; // 以任务状态为基准减少5px
  const commonInputStyle = {
    height: commonHeight,
    fontSize: '0.875rem',
    fontWeight: 400,
    borderRadius: '60px',
    '& .MuiSelect-select': {
      paddingTop: '8px',
      paddingBottom: '8px'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: '60px'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main
    }
  };
  
  const commonLabelStyle = {
    fontSize: '0.875rem',
    fontWeight: 500
  };
  
  return (
    <Paper sx={{ p: 2, mb: 0, borderRadius: '60px',backgroundColor: theme.palette.background.paper }}>
      <Grid container spacing={2} alignItems="center">
        {/* 日期范围 */}
        <Grid item xs={12} sm={6} md={3}>
          <CustomDatePicker
            label="开始日期"
            name="startDate" 
            value={localFilters.startDate ? new Date(localFilters.startDate) : null}
            onChange={(date) => handleDateChange('startDate', date)}
            fullWidth
            {...getStartDateLimits()}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <CustomDatePicker
            label="结束日期"
            name="endDate"
            value={localFilters.endDate ? new Date(localFilters.endDate) : null}
            onChange={(date) => handleDateChange('endDate', date)}
            fullWidth
            {...getEndDateLimits()}
          />
        </Grid>
        
        {/* 语种选择 */}
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="language-select-label" sx={{
              ...commonLabelStyle,
              color: theme.palette.primary.main
            }}>翻译语种</InputLabel>
            <Select
              labelId="language-select-label"
              name="language"
              value={localFilters.language}
              label="翻译语种"
              onChange={handleChange}
              sx={{
                ...commonInputStyle,
                '& .MuiInputLabel-root': {
                  color: theme.palette.primary.main
                }
              }}
            >
              <MenuItem value="all" sx={{ fontSize: '0.875rem', fontWeight: 400 }}>全部语种</MenuItem>
              {languages.map((lang) => (
                <MenuItem key={lang.code} value={lang.code} sx={{ fontSize: '0.875rem', fontWeight: 400 }}>
                  {lang.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        {/* 状态选择 */}
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="status-select-label" sx={{
              ...commonLabelStyle,
              color: theme.palette.primary.main
            }}>任务状态</InputLabel>
            <Select
              labelId="status-select-label"
              name="status"
              value={localFilters.status}
              label="任务状态"
              onChange={handleChange}
              sx={{
                ...commonInputStyle,
                '& .MuiInputLabel-root': {
                  color: theme.palette.primary.main
                }
              }}
            >
              <MenuItem value="all" sx={{ fontSize: '0.875rem', fontWeight: 400 }}>全部状态</MenuItem>
              {statuses.map((status) => (
                <MenuItem key={status.code} value={status.code} sx={{ fontSize: '0.875rem', fontWeight: 400 }}>
                  {status.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        {/* 操作按钮 */}
        <Grid item xs={12} sm={6} md={2}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<FilterAltIcon />}
              onClick={handleFilter}
              fullWidth
              sx={{ 
                borderRadius: '60px',
                height: commonHeight,
                fontSize: '0.875rem',
                fontWeight: 500,
                textTransform: 'none'
              }}
            >
              筛选
            </Button>
            <Button
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleReset}
              fullWidth
              sx={{ 
                borderRadius: '60px',
                height: commonHeight,
                fontSize: '0.875rem',
                fontWeight: 500,
                textTransform: 'none'
              }}
            >
              重置
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default TaskFilter; 